import { useCallback, useEffect, useRef } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationsBadge } from '@/contexts/NotificationsBadgeContext';
import { registerApnsToken } from '@/api/pushNotifications';
import { getStoredApnsToken, setStoredApnsToken } from '@/lib/apnsTokenStore';
import {
  getRouteFromPushNotificationData,
  unwrapPushPayloadData,
} from '@/utils/pushNotificationRouting';
import { getPlatform } from '@/lib/platform';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_PUSH_OPENED,
  EVENT_PUSH_RECEIVED,
  EVENT_PUSH_TOKEN_REGISTERED,
} from '@/lib/analyticsEvents';
import { debug, error } from '@/utils/logger';

async function syncApnsTokenWithBackend(token: string): Promise<void> {
  try {
    await registerApnsToken(token);
    WebAnalytics.track(EVENT_PUSH_TOKEN_REGISTERED);
    debug('[useNativePushNotifications] APNS token registered with backend');
  } catch (err) {
    error('[useNativePushNotifications] Failed to register APNS token', err);
  }
}

async function ensurePushRegistration(isAuthenticated: () => boolean): Promise<void> {
  if (getPlatform() !== 'ios') return;

  const status = await PushNotifications.checkPermissions();
  if (status.receive !== 'granted') {
    debug('[useNativePushNotifications] Push permission not granted', { receive: status.receive });
    return;
  }

  const existingToken = getStoredApnsToken();
  if (existingToken && isAuthenticated()) {
    await syncApnsTokenWithBackend(existingToken);
    return;
  }

  await PushNotifications.register();
}

/**
 * iOS-only native push listeners. Mount once near the app root (before auth resolves).
 * Registers the device token listener early; POSTs when authenticated.
 */
export function useNativePushNotifications(): void {
  const { isAuthenticated, isInitialized } = useAuth();
  const { refreshUnreadCount } = useNotificationsBadge();
  const router = useNavigate();
  const listenersAttachedRef = useRef(false);
  const listenersReadyRef = useRef<Promise<void> | null>(null);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const isInitializedRef = useRef(isInitialized);
  const pendingPushRouteRef = useRef<string | null>(null);

  isAuthenticatedRef.current = isAuthenticated;
  isInitializedRef.current = isInitialized;

  const navigateFromPush = useCallback(
    (route: string, source: 'tap' | 'pending'): void => {
      debug('[useNativePushNotifications] Navigating from push', { route, source });
      window.setTimeout(() => {
        router(route);
      }, source === 'pending' ? 500 : 0);
    },
    [router]
  );

  const handlePushNotificationAction = useCallback(
    (event: { actionId?: string; notification?: { data?: Record<string, unknown> } }) => {
      const rawData = event.notification?.data;
      const payload = unwrapPushPayloadData(rawData);
      const route = getRouteFromPushNotificationData(rawData);

      debug('[useNativePushNotifications] Push tap', {
        actionId: event.actionId,
        payload,
        route,
      });

      WebAnalytics.track(EVENT_PUSH_OPENED, {
        deep_link_route: route ?? undefined,
        push_notification_type: payload.type,
      });

      if (!route) return;

      if (!isInitializedRef.current) {
        pendingPushRouteRef.current = route;
        return;
      }

      navigateFromPush(route, 'tap');
    },
    [navigateFromPush]
  );

  useEffect(() => {
    if (getPlatform() !== 'ios') return;
    if (!isInitialized || !pendingPushRouteRef.current) return;

    const route = pendingPushRouteRef.current;
    pendingPushRouteRef.current = null;
    navigateFromPush(route, 'pending');
  }, [isInitialized, navigateFromPush]);

  useEffect(() => {
    if (getPlatform() !== 'ios') return;
    if (listenersAttachedRef.current) return;
    listenersAttachedRef.current = true;

    const listenerHandles: Array<{ remove: () => Promise<void> }> = [];

    listenersReadyRef.current = (async () => {
      try {
        listenerHandles.push(
          await PushNotifications.addListener('registration', (token) => {
            const value = token.value?.trim();
            if (!value) return;
            setStoredApnsToken(value);
            debug('[useNativePushNotifications] Device token received', { length: value.length });
            if (isAuthenticatedRef.current) {
              void syncApnsTokenWithBackend(value);
            }
          })
        );

        listenerHandles.push(
          await PushNotifications.addListener('registrationError', (err) => {
            error('[useNativePushNotifications] Registration error', err);
          })
        );

        listenerHandles.push(
          await PushNotifications.addListener('pushNotificationReceived', () => {
            WebAnalytics.track(EVENT_PUSH_RECEIVED);
            void refreshUnreadCount();
          })
        );

        listenerHandles.push(
          await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
            handlePushNotificationAction(event);
          })
        );
      } catch (err) {
        error('[useNativePushNotifications] Failed to attach listeners', err);
        listenersAttachedRef.current = false;
        listenersReadyRef.current = null;
        throw err;
      }
    })();

    return () => {
      void Promise.all(listenerHandles.map((handle) => handle.remove())).catch((err) => {
        error('[useNativePushNotifications] Failed to remove listeners', err);
      });
      listenersAttachedRef.current = false;
      listenersReadyRef.current = null;
    };
  }, [refreshUnreadCount, handlePushNotificationAction]);

  useEffect(() => {
    if (getPlatform() !== 'ios') return;
    if (!isAuthenticated) return;

    void (async () => {
      try {
        await listenersReadyRef.current;
        await ensurePushRegistration(() => isAuthenticatedRef.current);
      } catch (err) {
        error('[useNativePushNotifications] Failed to ensure push registration', err);
      }
    })();
  }, [isAuthenticated]);
}
